import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { UploadedFile, formatTime } from "@/lib/tape";

interface Props {
  file: UploadedFile;
  onChange: (patch: Partial<UploadedFile>) => void;
}

type DragMode = "start" | "end" | "seek" | null;

export function VideoTrimmer({ file, onChange }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragMode>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    setMuted(false);
    const v = videoRef.current;
    if (v) v.muted = false;
  }, [file.id]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrent(v.currentTime);
      if (v.currentTime >= file.trimEnd) {
        v.pause();
        v.currentTime = file.trimStart;
        setPlaying(false);
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [file.trimEnd, file.trimStart]);

  function toggle() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime < file.trimStart || v.currentTime >= file.trimEnd) {
        v.currentTime = file.trimStart;
      }
      v.play();
    } else {
      v.pause();
    }
  }

  const dur = file.durationSec || 0;

  const pct = (t: number) => (dur > 0 ? (t / dur) * 100 : 0);

  const timeFromEvent = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || dur <= 0) return 0;
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return ratio * dur;
    },
    [dur],
  );

  const handlePointerDown = (mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = mode;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    applyDrag(mode, e.clientX);
  };

  function applyDrag(mode: DragMode, clientX: number) {
    if (!mode) return;
    const t = timeFromEvent(clientX);
    if (mode === "start") {
      const start = Math.max(0, Math.min(t, file.trimEnd - 0.5));
      onChange({ trimStart: start });
      if (videoRef.current) videoRef.current.currentTime = start;
      setCurrent(start);
    } else if (mode === "end") {
      const end = Math.min(dur, Math.max(t, file.trimStart + 0.5));
      onChange({ trimEnd: end });
      if (videoRef.current) videoRef.current.currentTime = end;
      setCurrent(end);
    } else {
      const ct = Math.max(0, Math.min(t, dur));
      if (videoRef.current) videoRef.current.currentTime = ct;
      setCurrent(ct);
    }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    applyDrag(dragRef.current, e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    dragRef.current = null;
  };

  function scheduleHide() {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 1800);
  }

  function revealControls() {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setShowControls(true);
    scheduleHide();
  }

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const startPct = pct(file.trimStart);
  const endPct = pct(file.trimEnd);
  const curPct = pct(Math.min(current, dur));

  // Two copies of barsBlock render (mobile/desktop), but only the visible one
  // should provide geometry for drag calculations.
  const trackRefCb = useCallback((el: HTMLDivElement | null) => {
    if (el && el.offsetParent !== null) {
      trackRef.current = el;
    }
  }, []);

  const barsBlock = (
    <>
      {/* Trim bar */}
      <div className="text-[11px] leading-none text-white/90 max-md:text-muted-foreground mb-1">Trim</div>
      <div
        ref={trackRefCb}
        className="relative h-6 cursor-pointer touch-none select-none"
        onPointerDown={(e) => {
          // clicks on the bare track shouldn't move trim handles; do nothing
          e.preventDefault();
        }}
      >

        {/* full track */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-white/40 max-md:bg-border" />
        {/* trimmed (selected) region */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 bg-primary"
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        />
        {/* trim start handle */}
        <div
          role="slider"
          aria-label="Trim start"
          aria-valuemin={0}
          aria-valuemax={dur}
          aria-valuenow={file.trimStart}
          tabIndex={0}
          onPointerDown={handlePointerDown("start")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-[18px] rounded-full bg-white border-2 border-primary cursor-ew-resize touch-none"
          style={{ left: `${startPct}%` }}
        />
        {/* trim end handle */}
        <div
          role="slider"
          aria-label="Trim end"
          aria-valuemin={0}
          aria-valuemax={dur}
          aria-valuenow={file.trimEnd}
          tabIndex={0}
          onPointerDown={handlePointerDown("end")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-[18px] rounded-full bg-white border-2 border-primary cursor-ew-resize touch-none"
          style={{ left: `${endPct}%` }}
        />
      </div>

      {/* Playback bar */}
      <div className="text-[11px] leading-none text-white/90 max-md:text-muted-foreground mt-2 mb-1">Playback</div>
      <div
        className="relative h-4 cursor-pointer touch-none select-none"
        onPointerDown={handlePointerDown("seek")}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-white/40 max-md:bg-border" />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-2 rounded-full bg-white max-md:bg-foreground pointer-events-none"
          style={{ left: `${curPct}%` }}
        />
      </div>
    </>
  );

  return (
    <div className="space-y-2 md:space-y-3">
      <div
        ref={containerRef}
        className="relative rounded-xl overflow-hidden bg-black aspect-video group"
        onMouseEnter={revealControls}
        onMouseMove={revealControls}
        onMouseLeave={() => {
          if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
          if (!dragRef.current) setShowControls(false);
        }}
      >
        <video
          ref={videoRef}
          src={file.url}
          className="w-full h-full object-contain"
          playsInline
          onClick={toggle}
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const v = videoRef.current;
            if (!v) return;
            const next = !muted;
            v.muted = next;
            setMuted(next);
          }}
          aria-label={muted ? "Unmute" : "Mute"}
          className={`absolute top-2 right-2 size-9 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80 transition-opacity max-md:opacity-100 ${
            showControls ? "opacity-100" : "opacity-0"
          }`}
        >
          {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
        </button>

        {/* Desktop-only overlay: bars + play/time, revealed on hover */}
        <div
          className={`hidden md:block absolute inset-x-0 bottom-0 px-3 pt-8 pb-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-200 ${
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {barsBlock}

          {/* Bottom bar: play + time */}
          <div className="flex items-center justify-between gap-3 text-white mt-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              aria-label={playing ? "Pause" : "Play"}
              className="size-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors"
            >
              {playing ? (
                <Pause className="size-4" />
              ) : (
                <Play className="size-4 ml-0.5" />
              )}
            </button>
            <div className="text-xs font-mono tabular-nums text-white/90">
              {formatTime(current)} / {formatTime(dur)}
            </div>
          </div>
        </div>

        {/* Mobile-only play/time overlay (bars are below video on mobile) */}
        <div className="md:hidden absolute inset-x-0 bottom-0 px-3 pt-8 pb-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-center justify-between gap-3 text-white">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            aria-label={playing ? "Pause" : "Play"}
            className="size-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors"
          >
            {playing ? (
              <Pause className="size-4" />
            ) : (
              <Play className="size-4 ml-0.5" />
            )}
          </button>
          <div className="text-xs font-mono tabular-nums text-white/90">
            {formatTime(current)} / {formatTime(dur)}
          </div>
        </div>
      </div>

      {/* Mobile-only bars below the video */}
      <div className="md:hidden rounded-lg border border-border bg-card p-3">
        {barsBlock}
      </div>

      {/* Precision reference */}
      <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
        <span>Trim start: {formatTime(file.trimStart)}</span>
        <span>Duration: {formatTime(Math.max(0, file.trimEnd - file.trimStart))}</span>
        <span>Trim end: {formatTime(file.trimEnd)}</span>
      </div>
    </div>
  );
}

